/**
 * Per-innings form.
 *
 * Ratings say what a player is capable of; form says what kind of day they are
 * having. Rolled once per player per innings and held constant for it, so a
 * batter who is "on song" stays on song for their stay at the crease rather
 * than flickering ball to ball.
 *
 * This is what makes a 99-rated player genuinely able to fail, which the design
 * requires -- without it, elite players become boringly deterministic and the
 * draft loses its tension.
 *
 * ## Cap winners get a floor, not a boost
 *
 * The Orange and Purple Cap holders already carry high ratings. Adding a flat
 * stat bonus would double-count and inflate scores. Instead their form roll has
 * a raised *floor*: they can have an ordinary day, rarely a disastrous one.
 * Their mean output barely moves; their downside shrinks. That is the honest
 * reading of "proven performer" -- consistency is exactly what winning a cap
 * demonstrates -- and it cannot be exploited into absurd totals.
 */

import type { Coefficients } from './coefficients.ts';
import type { Player } from './types.ts';
import type { Rng } from './rng.ts';

/** Form is a multiplier on skill, centred on 1. */
export type Form = number;

/**
 * Standard normal via Box-Muller.
 *
 * Uses only one of the two values the transform produces. Caching the second
 * would save a draw but makes the RNG stream depend on call parity, which is a
 * miserable class of replay bug to chase.
 */
function standardNormal(rng: Rng): number {
  // Guard against log(0); u is in [0, 1).
  const u = 1 - rng.float();
  const v = rng.float();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Rolls one player's form for one innings.
 *
 * Clamped at +/-2.5 standard deviations. A normal distribution has unbounded
 * tails, and a form of 0.1 or 3.0 would produce a match nobody would believe.
 */
export function rollForm(player: Player, co: Coefficients, rng: Rng): Form {
  const raw = 1 + standardNormal(rng) * co.formSpread;
  const lo = 1 - 2.5 * co.formSpread;
  const hi = 1 + 2.5 * co.formSpread;
  const bounded = raw < lo ? lo : raw > hi ? hi : raw;

  const isCapWinner = player.isOrangeCap || player.isPurpleCap;
  if (isCapWinner && bounded < co.capFormFloor) return co.capFormFloor;

  return bounded;
}

/**
 * Rolls form for every player who might take part in an innings.
 *
 * Rolled in squad order from a dedicated stream so the result is reproducible
 * on replay and unaffected by how many balls are actually bowled.
 */
export function rollInningsForm(
  players: readonly Player[],
  co: Coefficients,
  rng: Rng,
): ReadonlyMap<string, Form> {
  const forms = new Map<string, Form>();
  for (const p of players) forms.set(p.id, rollForm(p, co, rng));
  return forms;
}
